import { useCallback, useEffect, useState } from 'react'
import { ContentMetadataText } from '@quebon/shared-learning/components/common/ContentMetadataText'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import {
  AlertCircle,
  ArrowRight,
  CalendarDays,
  ClipboardCheck,
} from 'lucide-react'
import todayStepCheckIcon from '@/assets/learner/today-step-check.svg'
import todayStepListIcon from '@/assets/learner/today-step-list.svg'
import todayStepLockIcon from '@/assets/learner/today-step-lock.svg'
import todayStepPlayIcon from '@/assets/learner/today-step-play.svg'
import { getTodayLesson } from '@/api/lcLearning'
import type { OfflineTestLockView } from '@/api/lcLearning'
import type { SpecialLectureLockView } from '@/api/unitAssessment'
import { specialLecturePath } from '@/hooks/learner/useSpecialLectureFlow'
import { ButtonPill } from '@/components/learner/ui/ButtonPill'
import { Button } from '@/components/learner/ui/Button'
import { Card } from '@/components/learner/ui/Card'
import { LearnerLoading } from '@/components/learner/ui/LearnerLoading'
import { Mascot } from '@/components/learner/ui/Mascot'
import { TestCardSurface } from '@/components/learner/test/TestLayout'
import { cn } from '@/lib/utils'
import { getLearnerErrorMessage } from '@/utils/errorMessage'
import { UnregisteredDeviceBanner } from '@/components/layout/UnregisteredDeviceBanner'
import { useLearnerChromeVisibility } from '@/components/layout/LearnerChrome'
import { MaterialNoticeBanner } from '@/components/learner/MaterialNoticeBanner'
import { CumulativeAssessmentNotice } from '@/components/learner/cumulative/CumulativeAssessmentNotice'
import { toTodayModel } from './model/todayModel'
import type { TodayModel, TodayStep, TodayUnitModel } from './ui-model/types'

const TODAY_STEP_STATUS_ICONS: Record<TodayStep['status'], string> = {
  done: todayStepCheckIcon,
  current: todayStepPlayIcon,
  locked: todayStepLockIcon,
}

function formatLearnerDate(d: Date, locale: string): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'long', day: 'numeric', weekday: 'long',
  }).format(d)
}

export default function TodayClass() {
  const { t, i18n } = useTranslation(['learner', 'common'])
  const [model, setModel] = useState<TodayModel | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [starting, setStarting] = useState(false)
  const [startError, setStartError] = useState<string | null>(null)
  const navigate = useNavigate()
  const isChapterTest = model?.kind === 'offlineTest' && model.lock.kind === 'CHAPTER'
  const isCumulativeAssessment = model?.kind === 'cumulativeAssessment'

  // 챕터 테스트와 누적 테스트는 진도를 잠그는 테스트 게이트라 같은 전체화면 집중 컨텍스트를 쓴다.
  useLearnerChromeVisibility(isChapterTest || isCumulativeAssessment)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const data = await getTodayLesson()
      setModel(toTodayModel(data))
    } catch (e) {
      console.error('getTodayLesson failed', e)
      setError(getLearnerErrorMessage(e, t))
    } finally {
      setLoading(false)
    }
  }, [t])

  useEffect(() => {
    load()
  }, [load])

  useEffect(() => {
    const id = window.setInterval(async () => {
      try {
        const data = await getTodayLesson()
        setModel(toTodayModel(data))
        setError(null)
      } catch {
        // 폴링 실패는 무시 — 다음 주기에 재시도
      }
    }, 30_000)
    return () => window.clearInterval(id)
  }, [])

  if (loading) {
    return (
      <PageFrame>
        <LearnerLoading />
      </PageFrame>
    )
  }

  if (error) {
    return (
      <PageFrame>
        <Card
          size="md"
          role="alert"
          aria-live="assertive"
          className="flex flex-col items-center gap-4 py-8 text-center"
        >
          <AlertCircle
            aria-label={t('aria.error')}
            className="size-10 text-lc-red-900"
          />
          <p className="text-lc-body-2 text-lc-gray-700">{error}</p>
          <Button variant="primary" size="M" onClick={load}>
            {t('common:retry')}
          </Button>
        </Card>
      </PageFrame>
    )
  }

  if (!model) {
    return (
      <PageFrame>
        <EmptyToday onBrowseLibrary={() => navigate('/my/library')} t={t} />
      </PageFrame>
    )
  }

  if (model.kind === 'moduleTest') {
    return (
      <PageFrame>
        <ModuleTestStub t={t} />
      </PageFrame>
    )
  }

  if (model.kind === 'specialLecture') {
    return (
      <PageFrame labelledBy="special-lecture-heading">
        <SpecialLecturePriority
          lock={model.lock}
          onContinue={() => navigate(specialLecturePath(model.lock))}
          t={t}
        />
      </PageFrame>
    )
  }

  if (model.kind === 'offlineTest') {
    if (model.lock.kind === 'CHAPTER') {
      return (
        <section
          aria-labelledby="offline-test-heading"
          className="flex min-h-full bg-lc-bg-secondary p-4 md:p-10"
        >
          <TestCardSurface>
            <ChapterTestNotice lock={model.lock} t={t} />
          </TestCardSurface>
        </section>
      )
    }
    return (
      <PageFrame labelledBy="offline-test-heading">
        <OfflineTestNotice lock={model.lock} t={t} />
      </PageFrame>
    )
  }

  if (model.kind === 'cumulativeAssessment') {
    return (
      <section
        aria-labelledby="cumulative-assessment-heading"
        className="flex min-h-full bg-lc-bg-secondary p-4 md:p-10"
      >
        <TestCardSurface>
          <CumulativeAssessmentNotice
            assessment={model.assessment}
            onMathHead={() => navigate(`/my/cumulative-assessments/${model.assessment.assessmentId}/math-head`)}
            t={t}
          />
        </TestCardSurface>
      </section>
    )
  }

  const unit: TodayUnitModel = model
  const allDone = unit.completedSteps === unit.totalSteps && unit.totalSteps > 0
  const currentStep = unit.steps.find((s) => s.status === 'current')

  const goLearn = async () => {
    if (starting) return
    setStarting(true)
    setStartError(null)
    try {
      // 교사가 화면 체류 중 특강을 배정할 수 있으므로 클릭 시점의 우선 학습을 다시 확인한다.
      const fresh = await getTodayLesson()
      if (fresh.specialLecture) {
        setModel(toTodayModel(fresh))
        navigate(specialLecturePath(fresh.specialLecture))
        return
      }
      if (fresh.offlineTest) {
        setModel(toTodayModel(fresh))
        return
      }
      if (fresh.cumulativeAssessment) {
        setModel(toTodayModel(fresh))
        return
      }
      navigate(`/my/learn/${fresh.lessonId}`)
    } catch (e) {
      setStartError(getLearnerErrorMessage(e, t))
    } finally {
      setStarting(false)
    }
  }
  const goCompletedStep = (stepId: string) =>
    navigate(`/my/learn/${unit.lessonId}?stepId=${stepId}`)

  return (
    <PageFrame labelledBy="today-heading">
      <div className="grid items-start gap-6 lg:grid-cols-[400px_minmax(0,536px)]">
        <div className="flex flex-col gap-6">
          <section className="flex min-h-[310px] flex-col gap-6 rounded-2xl bg-lc-bg-primary px-5 pb-6 pt-5 shadow-lc-v3-card">
            <div className="flex flex-col gap-2 px-2">
              <div className="flex items-center justify-between gap-3 text-lc-v3-body-2 font-semibold">
                <p className="min-w-0 truncate text-lc-text-tertiary">
                  <ContentMetadataText text={unit.chapterNumber != null && unit.moduleTitle
                    ? t('today.chapterTitle', {
                        chapter: unit.chapterNumber,
                        title: unit.moduleTitle,
                      })
                    : unit.moduleTitle} />
                </p>
                <p className="flex shrink-0 items-center gap-1.5 text-lc-text-link">
                  <CalendarDays className="size-4" aria-hidden="true" />
                  {formatLearnerDate(new Date(), i18n.language)}
                </p>
              </div>
              <h1
                id="today-heading"
                aria-label={unit.lessonSequence != null
                  ? `${unit.lessonSequence}. ${unit.lessonTitle}`
                  : unit.lessonTitle}
                className="flex flex-col gap-1 text-lc-v3-h3 font-bold text-lc-v3-gray-950"
              >
                {unit.lessonSequence != null ? <span>{unit.lessonSequence}.</span> : null}
                <span className="min-h-[68px]"><ContentMetadataText text={unit.lessonTitle} /></span>
              </h1>
            </div>

            <div className="flex shrink-0 flex-col gap-6">
              <div className="flex items-center gap-4 rounded-2xl bg-lc-v3-gray-100 p-4">
                <div className="min-w-0 flex-1">
                  <p className="text-lc-v3-body-2 font-semibold text-lc-text-primary">
                    {t('today.learningToday')}
                  </p>
                  <p className="mt-1 text-lc-v3-h5 font-bold text-lc-text-primary">
                    {t('today.completedSteps', { done: unit.completedSteps })}
                  </p>
                  <p className="mt-1 text-lc-v3-body-2 font-medium text-lc-text-tertiary">
                    {allDone
                      ? t(unit.awaitingAdvance ? 'today.awaitingAdvanceShort' : 'today.goodbye')
                      : t('today.remainingSteps', { count: unit.totalSteps - unit.completedSteps })}
                  </p>
                </div>
                <TodayProgressRing current={unit.completedSteps} total={unit.totalSteps} />
              </div>

              {unit.awaitingReviewNoteGrade ? (
                <p role="status" className="text-lc-v3-body-2 font-medium text-lc-text-secondary">
                  {t('today.awaitingReviewNoteGrade')}
                </p>
              ) : allDone ? null : (
                <Button className="w-full" variant="primary" size="L" onClick={goLearn} disabled={starting}>
                  {starting ? t('today.checking') : t(unit.hasStarted ? 'today.resume' : 'today.start')}
                </Button>
              )}
              {startError ? (
                <p role="alert" className="text-lc-v3-body-2 font-medium text-lc-text-error">
                  {startError}
                </p>
              ) : null}
            </div>
          </section>

          <MaterialNoticeBanner notices={unit.materialNotices} />
        </div>

        <section
          aria-labelledby="steps-heading"
          className="flex min-h-[480px] flex-col gap-3 rounded-[20px] bg-lc-bg-primary px-3 py-5"
        >
          <header className="flex shrink-0 items-center justify-between pl-2 pr-4">
            <div className="flex min-w-0 items-center gap-2">
              <span className="inline-flex size-8 shrink-0 items-center justify-center rounded-lg bg-lc-key-50 text-lc-key-500">
                <img
                  src={todayStepListIcon}
                  alt=""
                  className="h-[15.667px] w-[17.333px]"
                />
              </span>
              <h2 id="steps-heading" className="text-lc-v3-h5 font-semibold text-lc-text-primary">
                {t('today.stepsHeader')}
              </h2>
            </div>
            <span className="flex items-center gap-1 text-lc-v3-h5 whitespace-nowrap">
              <strong className="font-bold text-lc-text-primary">{unit.completedSteps}</strong>
              <span className="font-semibold text-lc-text-tertiary">/ {unit.totalSteps}</span>
            </span>
          </header>
          <ol className="scrollbar-none flex min-h-0 flex-1 flex-col gap-2.5 overflow-y-auto rounded-lg px-2 pt-4">
            {unit.steps.map((step, index) => (
              <TodayStepItem
                key={step.id}
                number={index + 1}
                step={step}
                currentLabel={t('today.currentBadge')}
                onClick={
                  step.status === 'done'
                    ? () => goCompletedStep(step.id)
                    : step.id === currentStep?.id
                      ? goLearn
                      : undefined
                }
              />
            ))}
          </ol>
        </section>
      </div>
    </PageFrame>
  )
}


function TodayProgressRing({ current, total }: { current: number; total: number }) {
  const percentage = total > 0 ? Math.min(100, Math.max(0, (current / total) * 100)) : 0
  return (
    <div
      role="progressbar"
      aria-valuenow={current}
      aria-valuemin={0}
      aria-valuemax={total}
      className="inline-flex size-20 shrink-0 items-center justify-center rounded-full"
      style={{
        background: `conic-gradient(var(--color-lc-key-500) 0 ${percentage}%, var(--color-lc-v3-gray-300) 0)`,
      }}
    >
      <div className="flex size-[68px] items-center justify-center gap-1 rounded-full bg-lc-v3-gray-100 text-lc-v3-h5">
        <strong className="font-bold text-lc-text-primary">{current}</strong>
        <span className="font-semibold text-lc-text-tertiary">/ {total}</span>
      </div>
    </div>
  )
}

function TodayStepItem({
  number,
  step,
  currentLabel,
  onClick,
}: {
  number: number
  step: TodayStep
  currentLabel: string
  onClick?: () => void
}) {
  const clickable = step.status !== 'locked' && onClick != null
  return (
    <li>
      <button
        type="button"
        onClick={clickable ? onClick : undefined}
        disabled={!clickable}
        className={cn(
          'flex w-full items-center justify-between rounded-2xl border p-4 text-left',
          'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-lc-key-500',
          step.status === 'done' && 'border-lc-disabled-border bg-lc-bg-primary',
          step.status === 'current' && 'border-2 border-lc-key-500 bg-lc-bg-key-light shadow-lc-v3-active',
          step.status === 'locked' && 'border-lc-disabled-border bg-lc-disabled-bg',
        )}
      >
        <span className="flex min-w-0 flex-1 items-center gap-4">
          <span
            className={cn(
              'w-14 shrink-0 text-lc-v3-body-2 font-bold',
              step.status === 'done' && 'text-lc-success-500',
              step.status === 'current' && 'text-lc-key-600',
              step.status === 'locked' && 'text-lc-text-disabled',
            )}
          >
            STEP {number}
          </span>
          <span
            className={cn(
              'truncate text-lc-v3-h5 font-semibold',
              step.status === 'done' && 'text-lc-v3-gray-800',
              step.status === 'current' && 'font-bold text-lc-v3-gray-950',
              step.status === 'locked' && 'text-lc-text-tertiary',
            )}
          >
            <ContentMetadataText text={step.title} />
          </span>
          {step.status === 'current' ? (
            <span className="shrink-0 rounded-full bg-lc-key-500 px-3 py-1.5 text-lc-v3-label-md font-semibold text-lc-text-on-key">
              {currentLabel}
            </span>
          ) : null}
        </span>
        <img
          src={TODAY_STEP_STATUS_ICONS[step.status]}
          alt=""
          aria-hidden="true"
          className="ml-3 size-8 shrink-0"
        />
      </button>
    </li>
  )
}

function OfflineTestNotice({
  lock,
  t,
}: {
  lock: OfflineTestLockView
  t: TFunction
}) {
  const title = t('today.offlineTest.chapter.title')
  const detailLabel = t('today.offlineTest.chapter.label', { chapter: lock.chapterOrdinal })
  const detailTitle = lock.chapterTitle

  return (
    <Card
      size="lg"
      role="status"
      className="flex flex-col items-center gap-4 py-10 text-center"
    >
      <span
        className="inline-flex size-14 items-center justify-center rounded-full bg-lc-orange-100 text-lc-orange-dark"
        aria-hidden="true"
      >
        <ClipboardCheck className="size-7" />
      </span>
      <div>
        <p className="text-lc-caption-1 font-extrabold tracking-[0.08em] text-lc-orange-dark">
          {t('today.offlineTest.eyebrow')}
        </p>
        <h1 id="offline-test-heading" className="mt-2 text-lc-headline-3 text-lc-gray-900">
          {title}
        </h1>
      </div>
      <div className="rounded-lc-lg bg-lc-gray-50 px-5 py-3">
        <p className="text-lc-caption-1 font-bold text-lc-gray-600">
          {detailLabel}
        </p>
        <p className="mt-1 text-lc-headline-4 font-extrabold text-lc-gray-900">
          <ContentMetadataText text={detailTitle} />
        </p>
      </div>
      <p className="max-w-xl text-lc-body-2 text-lc-gray-700">
        {t('today.offlineTest.desc')}
      </p>
    </Card>
  )
}

function ChapterTestNotice({
  lock,
  t,
}: {
  lock: OfflineTestLockView
  t: TFunction
}) {
  const chapterLabel = t('today.offlineTest.chapter.label', { chapter: lock.chapterOrdinal })
  const chapterHeading = lock.chapterTitle
    ? t('today.offlineTest.chapter.heading', {
        chapter: lock.chapterOrdinal,
        title: lock.chapterTitle,
      })
    : chapterLabel

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="flex flex-col items-center gap-2">
        <p className="text-lc-v3-h4 font-semibold text-lc-text-link">
          {chapterHeading}
        </p>
        <h1 id="offline-test-heading" className="text-lc-v3-h1 font-bold text-lc-text-primary">
          {t('today.offlineTest.chapter.title')}
        </h1>
      </div>
      <p className="whitespace-pre-line text-lc-v3-body-1 font-medium text-lc-text-secondary">
        {t('today.offlineTest.chapter.description')}
      </p>
      <div className="inline-flex items-center justify-center rounded-full bg-lc-key-50 px-3.5 py-2 text-lc-v3-label-lg font-medium text-lc-key-500">
        {t('today.offlineTest.chapter.count', { count: lock.itemCount })}
      </div>
    </div>
  )
}

function SpecialLecturePriority({
  lock,
  onContinue,
  t,
}: {
  lock: SpecialLectureLockView
  onContinue: () => void
  t: TFunction
}) {
  const ctaKey = lock.status === 'REVIEW_OPEN'
    ? 'today.specialLecture.ctaReview'
    : lock.status === 'SUBMITTED' || lock.status === 'GRADED'
      ? 'today.specialLecture.ctaWait'
      : lock.status === 'STARTED'
        ? 'today.specialLecture.ctaResume'
        : 'today.specialLecture.cta'
  const stateKey = lock.status === 'REVIEW_OPEN'
    ? 'today.specialLecture.stateReview'
    : lock.status === 'SUBMITTED' || lock.status === 'GRADED'
      ? 'today.specialLecture.stateWait'
      : lock.status === 'STARTED'
        ? 'today.specialLecture.stateResume'
        : 'today.specialLecture.stateReady'

  return (
    <section
      aria-labelledby="special-lecture-heading"
      className="relative isolate overflow-hidden rounded-[20px] border border-lc-v3-gray-300 bg-white shadow-lc-v3-card"
    >
      <div className="absolute inset-y-0 left-0 w-1.5 bg-lc-key-500" aria-hidden="true" />
      <div className="grid gap-6 p-5 pl-6 sm:p-7 sm:pl-8 lg:grid-cols-[minmax(0,1fr)_280px] lg:items-center lg:gap-10">
        <div className="flex min-w-0 items-start gap-4 sm:gap-5">
          <span className="inline-flex size-12 shrink-0 items-center justify-center rounded-2xl bg-lc-key-50 text-lc-key-500 sm:size-14">
            <ClipboardCheck className="size-7 sm:size-8" strokeWidth={1.8} aria-hidden="true" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="inline-flex rounded-full bg-lc-key-50 px-3 py-1 text-lc-v3-overline font-semibold text-lc-key-700">
                {t('today.specialLecture.eyebrow')}
              </span>
              <span className="inline-flex rounded-full bg-lc-v3-gray-100 px-3 py-1 text-lc-v3-overline font-semibold text-lc-text-secondary">
                {t(stateKey)}
              </span>
            </div>
            <h1
              id="special-lecture-heading"
              className="mt-3 text-lc-v3-h3 font-bold text-lc-text-primary sm:text-lc-v3-h2"
            >
              {t('today.specialLecture.title')}
            </h1>
            <p className="mt-2 text-lc-v3-body-1 font-medium text-lc-text-secondary">
              {t('today.specialLecture.desc')}
            </p>
          </div>
        </div>
        <div className="rounded-2xl bg-lc-v3-gray-100 p-4 sm:p-5">
          <p className="text-lc-v3-caption-1 font-semibold text-lc-text-tertiary">
            {t('today.specialLecture.label')}
          </p>
          <p className="mt-1 break-words text-lc-v3-h5 font-bold leading-snug text-lc-text-primary">
            {lock.setName}
          </p>
          <div className="mt-3 flex flex-wrap gap-2 text-lc-v3-label-sm font-semibold text-lc-text-secondary">
            <span className="rounded-full bg-white px-3 py-1.5">
              {t(lock.mode === 'ONLINE'
                ? 'today.specialLecture.modeOnline'
                : 'today.specialLecture.modePrint')}
            </span>
            {lock.itemCount != null && lock.itemCount > 0 ? (
              <span className="rounded-full bg-white px-3 py-1.5">
                {t('today.specialLecture.count', { count: lock.itemCount })}
              </span>
            ) : null}
          </div>
          <Button className="mt-5 w-full gap-2" size="L" onClick={onContinue}>
            {t(ctaKey)}
            <ArrowRight className="size-5" aria-hidden="true" />
          </Button>
        </div>
      </div>
    </section>
  )
}

function PageFrame({
  children,
  labelledBy,
}: {
  children: React.ReactNode
  labelledBy?: string
}) {
  return (
    <section
      aria-labelledby={labelledBy}
      className="mx-auto flex min-h-full w-full max-w-[1040px] flex-col px-4 pb-5 pt-6 md:px-10"
    >
      <UnregisteredDeviceBanner />
      {children}
    </section>
  )
}

function EmptyToday({ onBrowseLibrary, t }: { onBrowseLibrary: () => void; t: TFunction }) {
  return (
    <div className="flex flex-col items-center justify-center gap-5 py-20">
      <Mascot name="chaeli" shape="roundbox" size={120} decorative />
      <h1 className="text-lc-headline-3 text-lc-gray-900">{t('today.empty.title')}</h1>
      <p className="text-lc-body-2 text-lc-gray-700 text-center">{t('today.empty.desc')}</p>
      <ButtonPill onClick={onBrowseLibrary}>{t('today.empty.cta')}</ButtonPill>
    </div>
  )
}

function ModuleTestStub({ t }: { t: TFunction }) {
  return (
    <Card size="lg" className="flex flex-col items-center gap-4 py-10 text-center">
      <Mascot name="queda" shape="roundbox" size={80} decorative />
      <h1 className="text-lc-headline-3 text-lc-gray-900">{t('today.moduleTest.title')}</h1>
      <p className="text-lc-body-2 text-lc-gray-700">{t('today.moduleTest.desc')}</p>
    </Card>
  )
}
